import { BOOKING_API_URL, supabase } from '../lib/supabase';

// Re-send de cadeaubon-mail voor een betaalde voucher-aankoop. Dekt het gat
// waar de mail na betaling niet aankwam (bv. netwerkfout richting Resend) en
// de Stripe-webhook-retries op zijn, of waar de klant de mail kwijt is.
export interface ResendVoucherEmailResponse {
  ok: boolean;
  voucher_purchase_id: string;
  resend_id: string | null;
}

export interface ResendVoucherEmailError {
  error: string;
  message?: string;
}

export interface ResendVoucherEmailResult {
  ok: boolean;
  data: ResendVoucherEmailResponse | null;
  error: ResendVoucherEmailError | null;
  status: number;
}

// Identify the purchase either directly (Sales screen: synthetic voucher rows
// carry the voucher_purchases id) or via the minted discount code (Vouchers
// screen: rows are discount codes; the endpoint resolves the linked purchase).
export type ResendVoucherEmailRef =
  | { voucherPurchaseId: string }
  | { codeId: string };

export async function resendVoucherEmail(
  ref: ResendVoucherEmailRef,
): Promise<ResendVoucherEmailResult> {
  const { data: sessionData } = await supabase.auth.getSession();
  const jwt = sessionData.session?.access_token;
  if (!jwt) {
    return {
      ok: false,
      data: null,
      error: { error: 'unauthenticated', message: 'No active session' },
      status: 401,
    };
  }

  let res: Response;
  try {
    res = await fetch(`${BOOKING_API_URL}/booking-voucher-resend-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify(
        'voucherPurchaseId' in ref
          ? { voucher_purchase_id: ref.voucherPurchaseId }
          : { code_id: ref.codeId },
      ),
    });
  } catch (e) {
    return {
      ok: false,
      data: null,
      error: {
        error: 'network_error',
        message: e instanceof Error ? e.message : String(e),
      },
      status: 0,
    };
  }

  let json: unknown = null;
  try {
    json = await res.json();
  } catch {
    /* empty / non-JSON */
  }

  if (!res.ok) {
    return {
      ok: false,
      data: null,
      error: (json as ResendVoucherEmailError) ?? {
        error: 'unknown_error',
        message: `HTTP ${res.status}`,
      },
      status: res.status,
    };
  }

  return {
    ok: true,
    data: json as ResendVoucherEmailResponse,
    error: null,
    status: res.status,
  };
}
